import { useState, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// ==================== GraphQL Queries ====================

const GET_FACTURATION_MODE_CONFIG = gql`
  query GetFacturationModeConfig {
    getFacturationModeConfig {
      id
      enabled
      applyCashOnly
      applyConsumerOnly
      excludeMixedPayments
      excludeIdentified
      allowManualForce
      lastSyncAt
      lastSyncSuccess
      lastSyncError
      principalApiUrl
      lastChangedBy
      lastChangedAt
      createdAt
      updatedAt
    }
  }
`;

const GET_FACTURATION_MODE_LOGS = gql`
  query GetFacturationModeLogs($limit: Int) {
    getFacturationModeLogs(limit: $limit) {
      id
      action
      previousState
      newState
      changedBy
      changedByName
      reason
      ipAddress
      syncAttempted
      syncSuccess
      syncError
      createdAt
    }
  }
`;

const GET_FACTURATION_MODE_STATS = gql`
  query GetFacturationModeStats {
    getFacturationModeStats {
      totalChanges
      enabledCount
      disabledCount
      syncFailures
      lastChange
    }
  }
`;

const GET_PRINCIPAL_SYSTEM_STATUS = gql`
  query GetPrincipalSystemStatus {
    getPrincipalSystemStatus {
      available
      currentState
      error
    }
  }
`;

// ==================== GraphQL Mutations ====================

const TOGGLE_FACTURATION_MODE = gql`
  mutation ToggleFacturationMode($input: ToggleFacturationModeInput!) {
    toggleFacturationMode(input: $input) {
      config {
        id
        enabled
        lastSyncAt
        lastSyncSuccess
        lastSyncError
        lastChangedBy
        lastChangedAt
      }
      syncResult {
        success
        message
        error
      }
      log {
        id
        action
        previousState
        newState
        changedBy
        syncSuccess
        createdAt
      }
    }
  }
`;

const TOGGLE_ALLOW_MANUAL_FORCE = gql`
  mutation ToggleAllowManualForce($allowed: Boolean!, $changedBy: String!) {
    toggleAllowManualForce(allowed: $allowed, changedBy: $changedBy) {
      id
      enabled
      allowManualForce
    }
  }
`;

const RETRY_FACTURATION_MODE_SYNC = gql`
  mutation RetryFacturationModeSync($changedBy: String!) {
    retryFacturationModeSync(changedBy: $changedBy) {
      success
      message
      error
    }
  }
`;

// ==================== Types ====================

export interface FacturationModeConfig {
  id: string;
  enabled: boolean;
  applyCashOnly: boolean;
  applyConsumerOnly: boolean;
  excludeMixedPayments: boolean;
  excludeIdentified: boolean;
  allowManualForce: boolean;
  lastSyncAt: string | null;
  lastSyncSuccess: boolean;
  lastSyncError: string | null;
  principalApiUrl: string;
  lastChangedBy: string | null;
  lastChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FacturationModeLog {
  id: string;
  action: string;
  previousState: boolean | null;
  newState: boolean | null;
  changedBy: string;
  changedByName: string | null;
  reason: string | null;
  ipAddress: string | null;
  syncAttempted: boolean;
  syncSuccess: boolean | null;
  syncError: string | null;
  createdAt: string;
}

export interface FacturationModeStats {
  totalChanges: number;
  enabledCount: number;
  disabledCount: number;
  syncFailures: number;
  lastChange: string | null;
}

export interface PrincipalSystemStatus {
  available: boolean;
  currentState: boolean | null;
  error: string | null;
}

export interface SyncResult {
  success: boolean;
  message: string;
  error: string | null;
}

// ==================== Hooks ====================

/**
 * Hook para obtener la configuración del modo de facturación
 * @param pollInterval - Intervalo de polling en milisegundos (default: 5000ms = 5s)
 */
export function useFacturationModeConfig(pollInterval: number = 5000) {
  const { data, loading, error, refetch } = useQuery(GET_FACTURATION_MODE_CONFIG, {
    fetchPolicy: 'cache-and-network',
    pollInterval, // Actualizar automáticamente cada X ms
  });

  return {
    config: data?.getFacturationModeConfig as FacturationModeConfig | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para obtener los logs del modo de facturación
 */
export function useFacturationModeLogs(limit: number = 50) {
  const { data, loading, error, refetch } = useQuery(GET_FACTURATION_MODE_LOGS, {
    variables: { limit },
    fetchPolicy: 'cache-and-network',
  });

  return {
    logs: (data?.getFacturationModeLogs || []) as FacturationModeLog[],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para obtener las estadísticas del modo de facturación
 */
export function useFacturationModeStats() {
  const { data, loading, error, refetch } = useQuery(GET_FACTURATION_MODE_STATS, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    stats: data?.getFacturationModeStats as FacturationModeStats | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para verificar el estado del sistema principal
 */
export function usePrincipalSystemStatus() {
  const { data, loading, error, refetch } = useQuery(GET_PRINCIPAL_SYSTEM_STATUS, {
    fetchPolicy: 'network-only',
  });

  return {
    status: data?.getPrincipalSystemStatus as PrincipalSystemStatus | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para activar/desactivar el modo de facturación
 */
export function useToggleFacturationMode() {
  const [toggleMutation, { loading }] = useMutation(TOGGLE_FACTURATION_MODE);
  const [result, setResult] = useState<{
    config?: FacturationModeConfig;
    syncResult?: SyncResult;
    error?: string;
  } | null>(null);

  const toggle = useCallback(
    async (input: {
      enabled: boolean;
      changedBy: string;
      changedByName?: string;
      reason?: string;
    }) => {
      try {
        const response = await toggleMutation({
          variables: {
            input: {
              enabled: input.enabled,
              changedBy: input.changedBy,
              changedByName: input.changedByName,
              reason: input.reason,
            },
          },
          refetchQueries: [
            { query: GET_FACTURATION_MODE_CONFIG },
            { query: GET_FACTURATION_MODE_LOGS, variables: { limit: 50 } },
            { query: GET_FACTURATION_MODE_STATS },
          ],
        });

        const data = response.data?.toggleFacturationMode;
        setResult({
          config: data?.config,
          syncResult: data?.syncResult,
        });

        return data;
      } catch (error: any) {
        setResult({
          error: error.message || 'Error al cambiar el modo de facturación',
        });
        throw error;
      }
    },
    [toggleMutation]
  );

  return {
    toggle,
    loading,
    result,
  };
}

/**
 * Hook para reintentar la sincronización con el sistema principal
 */
export function useRetryFacturationModeSync() {
  const [retryMutation, { loading }] = useMutation(RETRY_FACTURATION_MODE_SYNC);
  const [result, setResult] = useState<SyncResult | null>(null);

  const retry = useCallback(
    async (changedBy: string) => {
      try {
        const response = await retryMutation({
          variables: { changedBy },
          refetchQueries: [
            { query: GET_FACTURATION_MODE_CONFIG },
            { query: GET_FACTURATION_MODE_LOGS, variables: { limit: 50 } },
          ],
        });

        const data = response.data?.retryFacturationModeSync;
        setResult(data);
        return data;
      } catch (error: any) {
        const errorResult: SyncResult = {
          success: false,
          message: 'Error al reintentar sincronización',
          error: error.message,
        };
        setResult(errorResult);
        throw error;
      }
    },
    [retryMutation]
  );

  return {
    retry,
    loading,
    result,
  };
}

/**
 * Hook para activar/desactivar el permiso de forzado manual
 */
export function useToggleAllowManualForce() {
  const [mutation, { loading }] = useMutation(TOGGLE_ALLOW_MANUAL_FORCE);

  const toggleForce = useCallback(
    async (allowed: boolean, changedBy: string = 'admin') => {
      const response = await mutation({
        variables: { allowed, changedBy },
        refetchQueries: [{ query: GET_FACTURATION_MODE_CONFIG }],
      });
      return response.data?.toggleAllowManualForce;
    },
    [mutation]
  );

  return { toggleForce, loading };
}

/**
 * Hook combinado para el panel de modo de facturación
 */
export function useFacturationModePanel() {
  const { config, loading: configLoading, refetch: refetchConfig } = useFacturationModeConfig();
  const { logs, loading: logsLoading, refetch: refetchLogs } = useFacturationModeLogs();
  const { stats, loading: statsLoading, refetch: refetchStats } = useFacturationModeStats();
  const { status, loading: statusLoading, refetch: refetchStatus } = usePrincipalSystemStatus();
  const { toggle, loading: toggling } = useToggleFacturationMode();
  const { retry, loading: retrying } = useRetryFacturationModeSync();

  const refetchAll = useCallback(() => {
    refetchConfig();
    refetchLogs();
    refetchStats();
    refetchStatus();
  }, [refetchConfig, refetchLogs, refetchStats, refetchStatus]);

  return {
    config,
    logs,
    stats,
    principalStatus: status,
    loading: configLoading || logsLoading || statsLoading || statusLoading,
    toggling,
    retrying,
    toggle,
    retry,
    refetch: refetchAll,
  };
}
